/**
 * Run-level rules an experiment may want to flip.
 *
 * `matchMin` is the single knob for the match-3 versus match-4 question. It
 * lives in one module and is read at call time rather than baked into literals
 * throughout the matcher, because "try 4 with the same seeds" should be a
 * one-line change, not a rewrite.
 *
 * Match-3 arrived from the original match-game concept. Match-4 may suit the
 * tactical board-war game better — it shifts the feel from *place a piece and
 * frequently get something* toward *construct something intentionally*. The
 * difference is testable, which is why this is a knob and not a decision.
 */
export const DEFAULT_MATCH_MIN = 4;

let matchMin = DEFAULT_MATCH_MIN;

export function getMatchMin(): number {
  return matchMin;
}

export function setMatchMin(n: number): void {
  // Below 2 there is no game; above 7 a run cannot exist on a seven-column
  // board without deliberate arrangement.
  matchMin = Math.min(7, Math.max(2, Math.round(n)));
}

export function resetRules(): void {
  matchMin = DEFAULT_MATCH_MIN;
  seedDensity = DEFAULT_SEED_DENSITY;
}

/**
 * Board population at fight start. Separate from board SIZE — an 8×10 well
 * with the same density as 7×9 may simply be under-seeded rather than
 * intrinsically sparse. This knob lets the A/B test vary density independently.
 */
export const DEFAULT_SEED_DENSITY = 2;

let seedDensity = DEFAULT_SEED_DENSITY;

export function getSeedDensity(): number {
  return seedDensity;
}

export function setSeedDensity(n: number): void {
  // 0 = empty well (no seeding). Above 6 the opening board becomes cluttered
  // on a 7-wide well; 8 is the hard ceiling.
  seedDensity = Math.min(8, Math.max(0, Math.round(n)));
}

/**
 * Tier ladder offsets from matchMin. Derived rather than fixed, so raising the
 * threshold carries the whole ladder with it:
 *
 *   matchMin     = Base
 *   matchMin + 1 = Charged
 *   matchMin + 2 = Surge
 *   matchMin + 3 = Aether
 *
 * At matchMin 4 on a seven-column board, Aether is a full-column run — which
 * feels like the right size of event for the size of the board.
 */
export const TIER_OFFSETS = {
  charged: 1,
  surge: 2,
  aether: 3,
} as const;